import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import DynamicBackground from '../components/DynamicBackground';
import ChatWindow from '../components/ChatWindow';
import { getCurrentUser, logout } from '../services/auth';

const DashboardContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

const Header = styled.header`
  width: 100%;
  padding: 20px;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
`;

const Logo = styled.h1`
  color: white;
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
  letter-spacing: -0.5px;
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid white;
`;

const UserName = styled.span`
  color: white;
  font-weight: 500;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
`;

const LogoutButton = styled(Button)`
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ChatContainer = styled.div`
  width: 100%;
  max-width: 800px;
  height: 80vh;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(5px);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const WelcomeMessage = styled.div`
  color: white;
  margin-bottom: 20px;
  text-align: center;
  font-size: 1.2rem;
`;

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Get current user on mount
    const currentUser = getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <DashboardContainer>
      <DynamicBackground />
      
      <Header>
        <Logo>What Gov</Logo>
        <UserProfile>
          {user?.pictureUrl && (
            <UserAvatar src={user.pictureUrl} alt={user.name} />
          )}
          <UserName>{user?.name}</UserName>
          <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
        </UserProfile>
      </Header>
      
      <Content>
        <WelcomeMessage>
          Welcome back, {user?.name || 'User'}! Ask me anything about UK government.
        </WelcomeMessage>
        <ChatContainer>
          <ChatWindow authenticated={true} />
        </ChatContainer>
      </Content>
    </DashboardContainer>
  );
}

export default Dashboard;
